import _ from 'lodash'
import * as z from 'zod'
import type { IdiomasDisponibles } from '@base/lib/rosettas'
import emisorEventosObjeto from '@base/lib/emisorEventosObjeto'
import { ErrorAumentado } from '@base/lib/error'
import { ErrorNotificableZod, i18nIconos, i18nTapi } from '@base/lib/erroresNotificables'
import { crearStorage } from '@base/lib/localforage'
import type { Perfil, Usuario } from '@boxmagic/tiposComunes'
import { UsuarioZod } from '@boxmagic/tiposComunes'
import { decodeToken } from '@comun/token-utils'

export const IngresoModosZod = z.enum(['ingreso', 'registro', 'recuperarPass'])
export type IngresoModos = z.infer<typeof IngresoModosZod>

const RespuestaConErrorZod = z.object({
  ok: z.literal(false),
  error: z.string()
})

// * Eventos
const emisorEventos = { ...emisorEventosObjeto }
const usuarioRef = ref<Usuario | null | undefined>(undefined)
const tokenRef = ref<string | undefined>(undefined)

// Estados computados
export const usuario = computed(() => usuarioRef.value)
export const token = computed(() => tokenRef.value)
export const usuarioID = computed(() => usuarioRef.value?.usuarioID)
export const email = computed(() => usuarioRef.value?.email)
export const perfil = computed(() => usuarioRef.value?.perfil)

const usuarioStorage = crearStorage('Usuario')

export async function recuperarToken() {
  try {
    const token = unref(tokenRef)
    if (token)
      return token

    const tokenRecuperado = await DatosPersistentesPersonal.leer('token') ?? undefined

    if (_.size(tokenRecuperado)) {
      tokenRef.value = tokenRecuperado
      UsuarioAPI.IntegrarToken(tokenRecuperado)
      return tokenRecuperado
    }

    const tokenDeCambioDeVersion523 = await usuarioStorage.getItem<string>('token')
    if (tokenDeCambioDeVersion523) {
      tokenRef.value = tokenDeCambioDeVersion523
      usuarioStorage.clear()
      UsuarioAPI.IntegrarToken(tokenDeCambioDeVersion523)
    }
    // Si el contexto indica web, y no hay token, obtener desde localStorage

    return tokenDeCambioDeVersion523
  } catch (e) {
    console.error('Error en recuperarToken', e)
  }
}

// API
const i18n = TrosettaAPI.crear({
  muyCorto: {
    es: 'Muy corto',
    en: 'Too short',
    pt: 'Muito curto'
  },
  necesario: {
    es: 'Necesario',
    en: 'Required',
    pt: 'Necessário'
  },
  formatoInvalido: {
    es: 'Formato inválido',
    en: 'Invalid format',
    pt: 'Formato inválido'
  },
  invalido: {
    es: 'Invalid',
    en: 'Invalid',
    pt: 'Inválido'
  }
})

const RespuestaLecturaZod = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    usuario: UsuarioZod,
    token: z.string()
  }),
  RespuestaConErrorZod
])
type RespuestaLectura = z.infer<typeof RespuestaLecturaZod>

const RegistroZod = z.object({
  nombre: z
    .string()
    .min(2, { message: i18n('muyCorto') }),
  apellido: z
    .string()
    .min(2, { message: i18n('muyCorto') }),
  email: InputEmailZod,
  pass: InputPassZod
})
type Registro = z.infer<typeof RegistroZod>



export const IngresoArgsZod = z.object({
  email: InputEmailZod,
  pass: InputPassZod
})

export type IngresoArgs = z.infer<typeof IngresoArgsZod>

const UsuarioIngresarOkTAPI = z.object({
  ok: z.literal(true),
  usuario: UsuarioZod,
  token: z.string()
})

const RespuestaIngresoZod = z.discriminatedUnion('ok', [
  UsuarioIngresarOkTAPI,
  RespuestaConErrorZod
])

type RespuestaIngreso = z.infer<typeof RespuestaIngresoZod>



const refrescandoUsuarioRef = ref<boolean>(false)
export const refrescandoUsuario = computed(() => unref(refrescandoUsuarioRef))

export const UsuarioAPI = {
  ...emisorEventos,

  IntegrarUsuario: function (usuario: Usuario) {
    const fx = 'IntegrarUsuario'
    consolo.log('🧩 ' + fx, usuario)
    usuarioRef.value = usuario
    UsuarioAPI.emit('usuario', usuario)
  },
  IntegrarToken: async function (token: string | undefined) {
    try {
      if (token) {
        const decodificado = decodeToken(token)
        if (decodificado.iss === 'boxmagic') {
          consolo.log('decodedificado.iss === "boxmagic"')
          return
        }
      }

      tokenRef.value = token
      if (!token)
        await DatosPersistentesPersonal.borrar('token')
      else {
        await DatosPersistentesPersonal.guardar('token', token)
        // Disparar migración silenciosa ed25519 en background (fire-and-forget)
        EventosDeAppAPI.emitirEventoDeApp('token:integrado', token)
      }
    } catch (e) {
      console.error('Error en guardarToken', token, 'error:', e)
    }
  },
  Registrar: async function Registrar(registro: Registro): Promise<RespuestaLectura | false> {
    const fx = 'Registrar'
    consolo.log('📡', fx)
    try {
      const { nombre, apellido, email, pass } = RegistroZod.parse(registro, { reportInput: true })

      const respuesta = await comoSiFueraPorAxios({
        url: `${contextoApp.buildConfig.apiUrl}/boxmagic/cuentas/crear`,
        method: 'post',
        data: { nombre, apellido, email, pass },
        headers: await HeadersBase()
      })

      const parseoRespuesta = RespuestaLecturaZod.safeParse(respuesta)
      if (!parseoRespuesta.success) {
        consolo.error(`${fx} parseoRespuesta`, parseoRespuesta)
        throw new Error('Error al parsear respuesta')
      }
      if (!parseoRespuesta.data.ok) {
        const errorParseado = parseoRespuesta.data.error

        NotificacionesEnApp.atencion({
          titulo: i18nTapi('falloElRegistro'),
          texto: i18nTapi(errorParseado),
          codigo: errorParseado,
          icono: i18nIconos[errorParseado]
        })
        return false
      }


      const { usuario: usuarioRecibido } = parseoRespuesta.data

      const parseoUsuario = UsuarioZod.safeParse(usuarioRecibido)
      if (!parseoUsuario.success) {
        consolo.error(`${fx} parseoUsuario`, parseoUsuario)
        throw new Error('Error al parsear usuario')
      }
      const gtmPush = useGtmPush()
      gtmPush('cuentaCreada', {
        nombreCompleto: [nombre, apellido].join(' '),
        nombre,
        apellido,
        email
      })

      return parseoRespuesta.data
    }
    catch (e) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      consolo.error(fx, e)
      throw new ErrorAumentado(fx, { error: e })
    }
    finally {
      consolo.log(fx, 'fin')
    }
  },
  Ingresar: async function Ingresar(ingreso: IngresoArgs): Promise<RespuestaIngreso> {
    const fx = 'Ingresar'
    consolo.log('📡', fx)
    consolo.log('ingreso', ingreso)
    try {
      const { email, pass } = IngresoArgsZod.parse(ingreso, { reportInput: true })
      const respuesta = await comoSiFueraPorAxios({
        url: `${contextoApp.buildConfig.apiUrl}/boxmagic/cuentas/ingresar`,
        method: 'post',
        data: { email, pass },
        headers: await HeadersBase()
      })

      consolo.log('respuesta', respuesta)


      const parseoRespuesta = RespuestaIngresoZod.safeParse(respuesta)

      if (!parseoRespuesta.success) {
        console.error(`${fx}: issues`, parseoRespuesta.error.issues)
        console.error(`${fx}: respuesta original`, respuesta)
        throw new Error('errorParseadoUsuario', {
          cause: parseoRespuesta.error
        })
      }

      const respuestaParseada = parseoRespuesta.data

      if (!respuestaParseada.ok) {
        NotificacionesEnApp.atencion({
          titulo: i18nTapi('falloElIngreso'),
          texto: i18nTapi(respuestaParseada.error),
          codigo: respuestaParseada.error,
          icono: i18nIconos[respuestaParseada.error]
        })
        return respuestaParseada
      }
      return respuestaParseada
    }
    catch (e) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      consolo.error(fx, e)
      throw new ErrorAumentado(fx, { error: e })
    } finally {
      consolo.log(fx, 'fin')
    }
  },
  Refrescar: async function Refrescar(): Promise<boolean> {
    const fx = 'Refrescar'
    consolo.log('📡', fx)
    try {
      refrescandoUsuarioRef.value = true
      consolo.log('refrescandoUsuario', unref(refrescandoUsuarioRef))
      const token = await recuperarToken()
      if (!token)
        return false

      // Trazar antigüedad del token
      try {
        const decodificado = decodeToken(token)
        const iat = decodificado?.iat
        if (typeof iat === 'number') {
          const horasDesdeEmision = (Date.now() / 1000 - iat) / 3600
          if (horasDesdeEmision > 8) {
            consolo.warn('[cuenta] Token tiene más de 8 horas de antigüedad, se solicitará renovación:', { horasDesdeEmision: Math.round(horasDesdeEmision * 100) / 100 })
          } else {
            consolo.log('[cuenta] Antigüedad del token:', { horasDesdeEmision: Math.round(horasDesdeEmision * 100) / 100 })
          }
        }
      } catch (e) {
        consolo.warn('[cuenta] No se pudo decodificar token para verificar antigüedad:', e)
      }

      const headers = await HeadersConAuth()

      const respuesta = await comoSiFueraPorAxios({
        url: `${contextoApp.buildConfig.apiUrl}/boxmagic/cuentas/refrescar`,
        method: 'get',
        headers
      })

      const RefrescarSesionRespuestaZod = z.discriminatedUnion('ok', [
        z.object({
          ok: z.literal(true),
          usuario: UsuarioZod,
          token: z.string().optional()
        }),
        RespuestaConErrorZod
      ])

      const parseoRespuesta = RefrescarSesionRespuestaZod.safeParse(respuesta)
      if (!parseoRespuesta.success) {
        consolo.error(`${fx}: error`, parseoRespuesta.error)
        consolo.error(`${fx}: respuesta original`, respuesta)
        throw new Error('errorParseado', {
          cause: parseoRespuesta.error
        })
      }

      if (!parseoRespuesta.data.ok) {
        const errorID = parseoRespuesta.data.error

        if (!['registroNoEncontrado', 'llaveroNoEncontrado'].includes(errorID)) {
          NotificacionesEnApp.atencion({
            titulo: i18nTapi('falloLaCargaDePerfil'),
            texto: i18nTapi(errorID),
            codigo: errorID,
            icono: i18nIconos[errorID]
          })
        }

        const erroresQueCierranSesion = ['tokenExpirado', 'cuentaEliminada']
        if (erroresQueCierranSesion.includes(errorID)) UsuarioAPI.CerrarSesion()

        return false
      }

      // Nota: integraciones centralizadas ya realizadas por IntegrarElementosFrescos

      return true
    }
    catch (e) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      consolo.error(fx, e)
      throw new ErrorAumentado(fx, { error: e })
    }
    finally {
      await esperar(50)
      refrescandoUsuarioRef.value = false
      consolo.log('refrescandoUsuario', unref(refrescandoUsuarioRef))
      consolo.log(fx, 'fin')
    }
  },
  IngresarConToken:
    async function IngresarConToken(
      tokenAUtilizar: string,
      forzar = false
    ): Promise<boolean> {
      const fx = 'IngresarConToken'
      consolo.log('📡', fx)
      try {
        if (!forzar) {
          // Si ya hay un usuario logueado con el mismo token, no se hace nada
          if (token.value && token.value === tokenAUtilizar)
            return true
        }
        // Si ya hay un usuario logueado, cerrar sesión ?
        if (token.value && usuario.value) await UsuarioAPI.CerrarSesion()

        const respuesta = await comoSiFueraPorAxios({
          url: `${contextoApp.buildConfig.apiUrl}/boxmagic/cuentas/refrescar`,
          headers: Object.assign(await HeadersBase(), {
            Authorization: `Bearer ${tokenAUtilizar}`
          })
        })

        const parserIngresoConToken = z.discriminatedUnion('ok', [
          z.object({
            ok: z.literal(true),
            usuario: UsuarioZod
          }),
          RespuestaConErrorZod
        ])

        const parseoRespuesta = parserIngresoConToken.safeParse(respuesta)
        if (!parseoRespuesta.success) {
          consolo.error(`${fx}: error`, parseoRespuesta.error)
          consolo.error(`${fx}: respuesta original`, respuesta)
          throw new Error('errorParseado', {
            cause: parseoRespuesta.error
          })
        }
        const respuestaParseada = parseoRespuesta.data

        if (!respuestaParseada.ok) {
          NotificacionesEnApp.atencion({
            titulo: i18nTapi('falloElIngreso'),
            texto: i18nTapi(respuestaParseada.error),
            codigo: respuestaParseada.error,
            icono: i18nIconos[respuestaParseada.error]
          })
          return false
        }

        // Nota: integraciones centralizadas ya realizadas por IntegrarElementosFrescos
        return true
      }
      catch (e) {
        if (e instanceof ErrorAumentado)
          throw e.trazar(fx)
        consolo.error(fx, e)
        throw new ErrorAumentado(fx, { error: e })
      }
      finally {
        consolo.log(fx, 'fin')
      }
    }
  ,
  CerrarSesion: async function CerrarSesion() {
    const fx = 'CerrarSesion'
    consolo.log('🔒', fx)

    try {
      await comoSiFueraPorAxios({
        url: `${contextoApp.buildConfig.apiUrl}/autenticacion/salir`,
        method: 'post',
        headers: await HeadersConAuth()
      }).catch(e => {
        consolo.warn(fx, 'Error al cerrar sesión en servidor', e)
      })

      await Promise.all([
        DatosPersistentesPersonal.borrar('token'),
        AlmacenamientoTemporal.vaciar(),
        UsuarioAPI.IntegrarToken(undefined)
      ])
      consolo.log(fx, '2')

      UsuarioAPI.emit('sesionCerrada')

      const usuarioConectado = unref(usuario)

      const gimID = unref('gimnasioID')

      if (usuarioConectado) {
        const gtmPush = useGtmPush()
        gtmPush('logout', {
          id: usuarioConectado.usuarioID,
          email: usuarioConectado.email,
          nombreCompleto: `${usuarioConectado.perfil.nombre} ${usuarioConectado.perfil.apellido}`,
          gimnasioID: gimID
        })
      }
      consolo.log(fx, '3')


      usuarioRef.value = null
      navigateTo('/')
    }
    finally {
      consolo.log(fx, 'fin')
    }
  }
  ,
  CambiarPass: {
    async pedirCodigo(email: string, passNuevo: string) {
      const fx = 'CambiarPass>pedirCodigo'
      consolo.log('📡', fx)
      try {
        const respuesta = await comoSiFueraPorAxios({
          url: `${contextoApp.buildConfig.apiUrl}/boxmagic/cuentas/cambiarPass/pedirCodigo`,
          method: 'post',
          data: { email, passNuevo },
          headers: await HeadersBase()
        })

        const parseoRespuesta = z.discriminatedUnion('ok', [
          z.object({
            ok: z.literal(true),
            emailEnviado: z.boolean()
          }),
          RespuestaConErrorZod
        ]).safeParse(respuesta)

        if (!parseoRespuesta.success) {
          consolo.error(`${fx}: error`, parseoRespuesta.error)
          consolo.error(`${fx}: respuesta original`, respuesta)
          throw new Error('errorParseado', {
            cause: parseoRespuesta.error
          })
        }

        const respuestaParseada = parseoRespuesta.data

        if (!respuestaParseada.ok) {
          if (respuestaParseada.error === 'emailNoRegistrado') {
            NotificacionesEnApp.error({
              titulo: i18nTapi('noSePudoSolicitarCambioDePassword'),
              texto: `${i18nTapi(
                'noExisteUnaCuentaConEsteEmail'
              )} "${email}" \r(${i18nTapi('peroPuedesCrearUna')})`,
              duracionSegundos: 40,
              icono: 'solar:mailbox-bold-duotone'
            })
          }
          return respuestaParseada
        }
        return respuestaParseada
      }
      finally {
        consolo.log(fx, 'fin')
      }
    },
    async conCodigo(email: string, codigo: string) {
      const fx = 'UsuarioAPI>cambiarPass>conCodigo'
      consolo.log('📡', fx)

      try {
        const respuesta = await comoSiFueraPorAxios({
          url: `${contextoApp.buildConfig.apiUrl}/boxmagic/cuentas/cambiarPass/conCodigo`,
          method: 'post',
          data: { email, codigo },
          headers: await HeadersBase()
        })

        const parserCambioDePass = z.discriminatedUnion('ok', [
          z.object({
            ok: z.literal(true)
          }),
          z.object({
            ok: z.literal(false),
            error: ErrorNotificableZod,
            intentosRestantes: z.number().optional()
          })
        ])

        const parseoRespuesta = parserCambioDePass.safeParse(respuesta)
        if (!parseoRespuesta.success) {
          consolo.error(`${fx}: error`, parseoRespuesta.error)
          consolo.error(`${fx}: respuesta original`, respuesta)
          throw new Error('errorParseado', {
            cause: parseoRespuesta.error
          })
        }
        const respuestaParseada = parseoRespuesta.data

        if (!respuestaParseada.ok) {
          NotificacionesEnApp.atencion({
            titulo: i18nTapi('cambioDePasswordFallido'),
            texto: i18nTapi(respuestaParseada.error),
            codigo: respuestaParseada.error,
            icono: {
              codigoInvalido: 'iconoir:password-error'
            }[respuestaParseada.error]
          })
        }

        return respuestaParseada
      }
      finally {
        consolo.log(fx, 'fin')
      }
    }
  },
  EliminarCuenta: {
    async pedirCodigo(pass: string) {
      const fx = 'EliminarCuenta>pedirCodigo'
      consolo.log('📡', fx)
      try {
        const respuesta = await comoSiFueraPorAxios({
          url: `${contextoApp.buildConfig.apiUrl}/boxmagic/cuentas/eliminar/pedirCodigo`,
          method: 'post',
          data: { pass },
          headers: await HeadersConAuth()
        })

        const parseoRespuesta = z.discriminatedUnion('ok', [
          z.object({
            ok: z.literal(true),
            emailEnviado: z.boolean()
          }),
          RespuestaConErrorZod
        ]).safeParse(respuesta)

        if (!parseoRespuesta.success) {
          consolo.error(`${fx}: error`, parseoRespuesta.error)
          consolo.error(`${fx}: respuesta original`, respuesta)
          throw new Error('errorParseado', {
            cause: parseoRespuesta.error
          })
        }

        const respuestaParseada = parseoRespuesta.data

        if (!respuestaParseada.ok) {
          if (respuestaParseada.error === 'emailNoRegistrado') {
            NotificacionesEnApp.error({
              titulo: i18nTapi('noSePudoSolicitarCambioDePassword'),
              texto: `${i18nTapi(
                'noExisteUnaCuentaConEsteEmail'
              )} "${email}" \r(${i18nTapi('peroPuedesCrearUna')})`,
              duracionSegundos: 40,
              icono: 'solar:mailbox-bold-duotone'
            })
          }
          return respuestaParseada
        }
        return respuestaParseada
      }
      finally {
        consolo.log(fx, 'fin')
      }
    },
    async conCodigo(codigo: string) {
      const fx = 'EliminarCuenta>conCodigo'
      consolo.log('📡', fx)

      try {
        const respuesta = await comoSiFueraPorAxios({
          url: `${contextoApp.buildConfig.apiUrl}/boxmagic/cuentas/eliminar/conCodigo`,
          method: 'post',
          data: { codigo },
          headers: await HeadersConAuth()
        })

        const parserCambioDePass = z.discriminatedUnion('ok', [
          z.object({ ok: z.literal(true) }),
          z.object({
            ok: z.literal(false),
            error: ErrorNotificableZod,
            intentosRestantes: z.number().optional()
          })
        ])

        const parseoRespuesta = parserCambioDePass.safeParse(respuesta)
        if (!parseoRespuesta.success) {
          consolo.error(`${fx}: error`, parseoRespuesta.error)
          consolo.error(`${fx}: respuesta original`, respuesta)
          throw new Error('errorParseado', {
            cause: parseoRespuesta.error
          })
        }
        const respuestaParseada = parseoRespuesta.data

        if (!respuestaParseada.ok) {
          NotificacionesEnApp.atencion({
            titulo: i18nTapi('cambioDePasswordFallido'),
            texto: i18nTapi(respuestaParseada.error),
            codigo: respuestaParseada.error,
            icono: {
              codigoInvalido: 'iconoir:password-error'
            }[respuestaParseada.error]
          })
        }

        return respuestaParseada
      }
      finally {
        consolo.log(fx, 'fin')
        UsuarioAPI.Refrescar()
      }
    }
  },
  UrlCargaAvatar: async function UrlCargaAvatar(): Promise<string> {
    const fx = 'UrlCargaAvatar'
    consolo.log('📡', fx)
    try {
      const respuesta = await comoSiFueraPorAxios({
        url: `${contextoApp.buildConfig.apiUrl}/boxmagic/usuario/cargaDeAvatar`,
        method: 'get',
        headers: await HeadersConAuth()
      })

      consolo.log(`${fx} r`, respuesta)

      const parseoRespuesta = z.discriminatedUnion('ok', [
        z.object({
          ok: z.literal(true),
          url: z.string()
        }),
        RespuestaConErrorZod
      ]).safeParse(respuesta)

      if (parseoRespuesta.success === false) {
        consolo.error(`${fx}: error`, parseoRespuesta.error)
        consolo.error(`${fx}: respuesta original`, respuesta)
        throw new Error('errorParseado', { cause: parseoRespuesta.error })
      }
      const respuestaParseada = parseoRespuesta.data
      if (!respuestaParseada.ok)
        throw respuestaParseada.error

      return respuestaParseada.url
    }
    catch (e) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      consolo.error(fx, e)
      throw new ErrorAumentado(fx, { error: e })
    }
    finally {
      consolo.log(fx, 'fin')
    }
  },
  EditarPerfil: async function EditarPerfil(edicionPerfil: Partial<Perfil>): Promise<boolean> {
    const fx = 'EditarPerfil'
    consolo.log('📡', fx)

    try {
      const respuesta = await comoSiFueraPorAxios({
        url: `${contextoApp.buildConfig.apiUrl}/boxmagic/usuario/perfil`,
        method: 'put',
        headers: await HeadersConAuth(),
        data: {
          perfil: JSON.parse(JSON.stringify(edicionPerfil))
        }
      })

      consolo.log(`${fx} respuesta`, respuesta)

      const parseado = z.discriminatedUnion('ok', [
        z.object({
          ok: z.literal(true),
          usuario: UsuarioZod
        }),
        RespuestaConErrorZod
      ]).safeParse(respuesta)

      if (!parseado.success) {
        // Reportar
        consolo.error('Error no configurado')
        consolo.warn('respuesta', respuesta)
        consolo.warn('parseado.error', parseado.error)
        return false
      }

      const resultado = parseado.data
      if (!resultado.ok) {
        const errorParseado = RespuestaConErrorZod.parse(resultado, { reportInput: true })

        NotificacionesEnApp.atencion({
          titulo: i18nTapi('falloLaEdicionDePerfil'),
          texto: i18nTapi(errorParseado.error),
          codigo: errorParseado.error,
          icono: i18nIconos[errorParseado.error]
        })
        return false
      }

      usuarioRef.value = resultado.usuario

      return resultado.ok
    }
    finally {
      consolo.log(fx, 'fin')
    }
  }
}